import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { custForgotPassword } from "../lib/api";
import Seo from "../components/Seo";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CustomerAccess() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", cif: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await custForgotPassword(form.email);
        toast.success("Si el email existe, le hemos enviado un enlace para acceder");
        setMode("login");
        return;
      }
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success("Bienvenido");
      nav(loc.state?.from || "/mi-cuenta");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error de acceso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <Seo title="Acceso clientes | FABRIPAN" description="Acceda a su cuenta de cliente FABRIPAN." noindex />
      <div className="flex gap-2 mb-6 border border-border rounded-sm p-1 bg-secondary">
        <button onClick={() => setMode("login")} className={`flex-1 py-2 text-sm font-medium rounded-sm transition-colors ${mode === "login" || mode === "forgot" ? "bg-white text-brand shadow-sm" : "text-muted-foreground"}`} data-testid="tab-login">Iniciar sesión</button>
        <button onClick={() => setMode("register")} className={`flex-1 py-2 text-sm font-medium rounded-sm transition-colors ${mode === "register" ? "bg-white text-brand shadow-sm" : "text-muted-foreground"}`} data-testid="tab-register">Crear cuenta</button>
      </div>

      <form onSubmit={submit} className="border border-border rounded-sm bg-white p-6 space-y-4">
        <h1 className="font-heading font-bold text-2xl tracking-tighter">{mode === "login" ? "Iniciar sesión" : mode === "forgot" ? "Recuperar acceso" : "Crear cuenta"}</h1>
        {mode === "forgot" && <p className="text-sm text-muted-foreground">Introduzca su email y le enviaremos un enlace para crear o restablecer su contraseña.</p>}
        {mode === "register" && (
          <>
            <input required placeholder="Nombre y apellidos" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="reg-name" />
            <input placeholder="Empresa" value={form.company} onChange={(e) => set("company", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="reg-company" />
            <input placeholder="CIF / NIF" value={form.cif} onChange={(e) => set("cif", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="reg-cif" />
          </>
        )}
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="access-email" />
        {mode !== "forgot" && (
          <input required type="password" placeholder="Contraseña" value={form.password} onChange={(e) => set("password", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="access-password" />
        )}
        <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-semibold py-3 rounded-sm transition-colors" data-testid="access-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? "Entrar" : mode === "forgot" ? "Enviar enlace" : "Crear cuenta")}
        </button>
        {mode === "login" && <button type="button" onClick={() => setMode("forgot")} className="w-full text-center text-xs text-brand hover:underline" data-testid="forgot-link">¿Olvidó su contraseña?</button>}
        {mode === "forgot" && <button type="button" onClick={() => setMode("login")} className="w-full text-center text-xs text-muted-foreground hover:underline">Volver a iniciar sesión</button>}
      </form>
      <p className="text-center text-xs text-muted-foreground mt-4">También puede realizar pedidos sin registrarse. <Link to="/tienda" className="text-brand">Ir a la tienda</Link></p>
    </div>
  );
}
